import { useEffect, useState } from 'react';
import { liveScreenColor, lookOf, showsPicture } from '@lib/livePolls/liveScreenLook';
import type { LiveState } from '@models/api';
import { BigScreenCover } from './BigScreenCover';
import { BigScreenJoin } from './BigScreenJoin';
import { BigScreenQuestion } from './BigScreenQuestion';
import { BigScreenRemoved } from './BigScreenRemoved';
import { BigScreenResults } from './BigScreenResults';
import { BigScreenStage } from './BigScreenStage';

export interface BigScreenProps {
  state: LiveState;
  /** E9b: three failed reads in a row; the last state stays. */
  reconnecting?: boolean;
  /** The host view's preview only: the QR on the host's picture can be dragged. */
  onMoveQr?: (qr: { x: number; y: number }) => void;
}

/** How often a picture that failed to load is tried again, off screen. */
const PICTURE_RETRY_MS = 30_000;

/**
 * The big screen for one live state: B1/B1b (join), B2/B2c (the open question), B3/B3b (results), E8b
 * (removed). Display only: the author's controls (`BigScreenAuthorBar`, `BigScreenPagingBar`) are drawn outside
 * the stage, so the room sees the same layout whoever is signed in. Drawn on a scaled 1280×720 stage, so the host
 * view's preview (FE-L1b) can reuse it in any box.
 */
export function BigScreen({ state, reconnecting = false, onMoveQr }: BigScreenProps) {
  // A picture that would not load: the room gets the join screen, not a black stage with a lone QR.
  const [failedImage, setFailedImage] = useState<string | null>(null);
  // A failure may be a blip (the venue's wifi on first load): the picture is tried again off screen when the
  // browser comes back online and every 30 s, and shows again once it loads. Off screen, so a picture that is gone
  // for good never flickers the room between the join screen and a black stage; at most 2 requests a minute.
  useEffect(() => {
    if (failedImage === null) return;
    let probe: HTMLImageElement | null = null;
    const retry = () => {
      if (probe) probe.onload = null;
      probe = new Image();
      probe.onload = () => {
        setFailedImage(current => (current === failedImage ? null : current));
      };
      probe.src = failedImage;
    };
    const timer = window.setInterval(retry, PICTURE_RETRY_MS);
    window.addEventListener('online', retry);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('online', retry);
      if (probe) probe.onload = null;
    };
  }, [failedImage]);
  return (
    <BigScreenStage>
      {renderState(state, reconnecting, onMoveQr, failedImage, setFailedImage)}
    </BigScreenStage>
  );
}

const renderState = (
  state: LiveState,
  reconnecting: boolean,
  onMoveQr: BigScreenProps['onMoveQr'],
  failedImage: string | null,
  onImageFailed: (image: string) => void,
) => {
  const look = lookOf(state);
  // Screen 2's colour also reaches the results' join panel, so the poll ends on the colour it started on.
  const startedColor = liveScreenColor(look.started_color);
  const image = look.image_url;
  if (image !== null && image !== failedImage && showsPicture(state)) {
    return (
      <BigScreenCover
        state={state}
        image={image}
        qr={{ x: look.qr_x, y: look.qr_y }}
        reconnecting={reconnecting}
        onMoveQr={onMoveQr}
        onImageError={() => {
          onImageFailed(image);
        }}
      />
    );
  }
  if (state.removed) return <BigScreenRemoved state={state} />;
  if (state.live_state === 'ended') {
    return <BigScreenResults state={state} reconnecting={reconnecting} color={startedColor} />;
  }
  if (state.live_state === 'live' && state.current_position !== null && state.question) {
    return (
      <BigScreenQuestion state={state} question={state.question} reconnecting={reconnecting} />
    );
  }
  return (
    <BigScreenJoin
      state={state}
      reconnecting={reconnecting}
      color={state.live_state === 'live' ? startedColor : liveScreenColor(look.waiting_color)}
    />
  );
};
